/**
 * Narrow raw wire blocks to known, renderable blocks — the client-side skip
 * authority for forward compatibility. An unknown `type`, or a known type missing
 * a required field, normalizes to `null` (skipped) so the modal never crashes or
 * blanks on a future/broken block. See specs/in-app-announcements/spec.md (AC-2.4/2.5).
 */

import { ANNOUNCEMENT_BLOCK_TYPES as T } from '../constants/announcements.constants';
import type {
  AnnouncementBlock,
  AnnouncementButtonAction,
  AnnouncementInAppAction,
  RawAnnouncementBlock,
} from '../types/announcements.types';

function normalizeAlign(value: unknown): 'left' | 'center' | undefined {
  return value === 'center' ? 'center' : value === 'left' ? 'left' : undefined;
}

/** Only the in-app action hints this client knows how to perform survive; the rest are dropped. */
function normalizeInAppAction(value: unknown): AnnouncementInAppAction | undefined {
  return value === 'accountSwitcher' ? 'accountSwitcher' : undefined;
}

/**
 * Malformed / unknown actions fall back to `dismiss` (AC-2.3). A recognized `inAppAction`
 * hint is preserved on top of the base `kind` (AC-2.3a); an unrecognized hint is dropped so
 * the button behaves exactly per its base `kind`.
 */
function normalizeButtonAction(value: unknown): AnnouncementButtonAction {
  if (value && typeof value === 'object') {
    const action = value as { kind?: unknown; url?: unknown; inAppAction?: unknown };
    const inAppAction = normalizeInAppAction(action.inAppAction);
    if (action.kind === 'url' && typeof action.url === 'string' && action.url.trim().length > 0) {
      return inAppAction
        ? { kind: 'url', url: action.url, inAppAction }
        : { kind: 'url', url: action.url };
    }
    if (inAppAction) return { kind: 'dismiss', inAppAction };
  }
  return { kind: 'dismiss' };
}

/**
 * @returns the narrowed block, or `null` if it should be skipped.
 */
export function normalizeBlock(raw: RawAnnouncementBlock | null | undefined): AnnouncementBlock | null {
  if (!raw || typeof raw !== 'object' || typeof raw.type !== 'string') return null;

  switch (raw.type) {
    case T.HEADING:
    case T.PARAGRAPH: {
      const text = typeof raw.text === 'string' ? raw.text : '';
      if (text.trim().length === 0) return null;
      return { type: raw.type, text, align: normalizeAlign(raw.align) };
    }
    case T.IMAGE: {
      const url = typeof raw.url === 'string' ? raw.url : '';
      if (url.trim().length === 0) return null;
      return {
        type: 'image',
        url,
        aspectRatio: typeof raw.aspectRatio === 'number' && raw.aspectRatio > 0 ? raw.aspectRatio : undefined,
        contentFit: raw.contentFit === 'cover' ? 'cover' : 'contain',
      };
    }
    case T.BUTTON: {
      const label = typeof raw.label === 'string' ? raw.label : '';
      if (label.trim().length === 0) return null;
      return {
        type: 'button',
        label,
        action: normalizeButtonAction(raw.action),
        variant: raw.variant === 'secondary' ? 'secondary' : 'primary',
      };
    }
    case T.SPACER:
      return { type: 'spacer', size: raw.size === 'sm' || raw.size === 'lg' ? raw.size : 'md' };
    default:
      // Unknown/future block type — skip silently.
      return null;
  }
}

/** Narrow a raw block list to renderable blocks, dropping unknown/malformed entries. */
export function normalizeBlocks(
  raw: RawAnnouncementBlock[] | null | undefined,
): AnnouncementBlock[] {
  if (!Array.isArray(raw)) return [];
  const out: AnnouncementBlock[] = [];
  for (const block of raw) {
    const normalized = normalizeBlock(block);
    if (normalized) out.push(normalized);
  }
  return out;
}
